/**
 * Sparkline — hand-rolled SVG, no library.
 *
 * Tufte principles applied:
 *   - "Intense, simple, word-sized graphic" — sized to fit a table cell line.
 *   - Data ink only: no axes, no labels, no gridlines.
 *   - Color carries meaning (hit = green, miss = muted), not decoration.
 *   - Small multiples by reuse: the same component repeated per row scans
 *     instantly for outliers.
 *
 * Props:
 *   points: [{ game_date, actual, hit }]   chronological (oldest -> newest)
 *   width?: number  default 60
 *   height?: number default 16
 *   line?: number   optional reference line (the prop line) — faint DASHED rule
 *   avg?:  number   optional second reference line (season average) — faint SOLID rule
 */

import React from 'react';

const COLORS = {
  hit:   '#22c55e',   // brand green
  miss:  '#475569',   // muted slate
  empty: '#1e293b',   // sub-muted background tick (no game played)
  line:  '#64748b',   // faint reference line (prop line, dashed)
  avg:   '#f59e0b',   // season-average reference line (solid, low opacity)
};

export default function Sparkline({
  points = [],
  width = 60,
  height = 16,
  line = null,
  avg = null,
  count = 10,   // show only the last N points (Tufte: small + dense)
}) {
  if (!points || points.length === 0) {
    return <span className="text-xs text-text-secondary opacity-50">—</span>;
  }

  // Take only the trailing `count` points
  const trimmed = points.length > count ? points.slice(-count) : points;
  const N = trimmed.length;
  const max = Math.max(1, ...trimmed.map((p) => p.actual ?? 0));
  // Layout: leave a 1px padding around to avoid clipping antialiased edges.
  const padX = 1;
  const padY = 1;
  const usableW = width - padX * 2;
  const usableH = height - padY * 2;
  // Each slot is a vertical strip; bar = 60% of slot, gap = 40%.
  const slot = usableW / Math.max(N, 1);
  const barW = Math.max(2, slot * 0.7);

  // Reference line positions (if provided)
  const yFor = (val) =>
    val !== null && val !== undefined && max > 0
      ? padY + usableH - (Math.min(val, max) / max) * usableH
      : null;
  const lineY = yFor(line);
  const avgY = yFor(avg);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {avgY !== null && (
        <line
          x1={padX}
          y1={avgY}
          x2={width - padX}
          y2={avgY}
          stroke={COLORS.avg}
          strokeWidth={0.75}
          opacity={0.55}
        />
      )}
      {lineY !== null && (
        <line
          x1={padX}
          y1={lineY}
          x2={width - padX}
          y2={lineY}
          stroke={COLORS.line}
          strokeWidth={0.5}
          strokeDasharray="1,1"
          opacity={0.5}
        />
      )}
      {trimmed.map((p, i) => {
        const v = p.actual ?? 0;
        const bh = Math.max(1, (v / max) * usableH);
        const x = padX + i * slot + (slot - barW) / 2;
        const y = padY + (usableH - bh);
        const color = v === 0 ? COLORS.empty : p.hit ? COLORS.hit : COLORS.miss;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={bh}
            fill={color}
            rx={0.5}
          >
            <title>{`${p.game_date}: ${v}${p.hit ? ' ✓' : ''}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}
